<template>
  <HiDialog
    width="90vw"
    title="Import CSV File"
    v-model="isOpen"
    :submit-txt="stage === 3 ? 'Import' : 'Next'"
    :clearBtn="true"
    @clear="clear"
    :submitDisabled="disableSubmit"
    @submit="submit"
  >
    <div v-if="stage === 1" class="loader-block">
      <form ref="fileForm" style="display:none">
        <input
          type="file"
          :multiple="multiple"
          :accept="accept"
          ref="input"
          @change="fileInputChange"
        />
      </form>
      <transition name="fade">
        <div
          v-if="!cvsKeys"
          class="flex-block-center ma-1 drop-area"
          style=""
          @dragenter="dragEnter"
          @dragover="dragOver"
          @drop="drop"
          @mouseout="isDragOver = false"
          :class="{ over: isDragOver }"
        >
          <v-btn @click="clickFileBtn" text>
            <v-icon>mdi-file-delimited-outline</v-icon>
            Select File
          </v-btn>
          <div>Or drop a CSV file here.</div>
          <div class="error-block" v-if="parseErr">{{ parseErr }}</div>
        </div>
      </transition>
      <transition name="fade">
        <v-simple-table dense fixed-header v-if="cvsKeys" height="60vh">
          <template v-slot:default>
            <thead>
              <tr>
                <th
                  v-for="(key, i) in cvsKeys"
                  :key="i + key"
                  :class="{
                    duplicate: duplicateMapping[i],
                    unmapped: !mapping[i]
                  }"
                >
                  <v-checkbox
                    v-model="colSelection[i]"
                    :label="key"
                    dense
                    class="check"
                  ></v-checkbox>
                  <select
                    class="select pb-1"
                    v-model="mapping[i]"
                    :disabled="!colSelection[i]"
                    @change="mapChange"
                  >
                    <option disabled selected :value="undefined">
                      - Mapping -
                    </option>
                    <option v-for="src in mappingSrc" :key="src" :value="src"
                      >{{ src }}
                    </option>
                  </select>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(val, index) in cvsData" :key="index + val">
                <td
                  v-for="(v, j) in val"
                  :key="j + index + v"
                  class="cell"
                  :class="{ selected: colSelection[j] }"
                >
                  {{ v }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </transition>
    </div>
    <transition name="fade">
      <div v-if="stage === 2" class="flex-block-center loader-block pa-5">
        <div class="error-block">
          <div v-for="err in this.dataErr" :key="err.error">
            {{ err.error
            }}<span v-if="err.index != null"> @Line {{ err.index + 2 }}</span>
          </div>
        </div>
        <v-btn @click="stage--" class="ma-4">Back</v-btn>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="stage === 3" class="flex-block-center loader-block pa-5">
        <div class="mb-3">Import total {{ this.data.length }} leads.</div>
        <v-select
          :items="projects"
          label="Select Project"
          outlined
          item-text="label"
          item-value="value"
          dense
          style="flex-grow:0;width:200px"
          v-model="project"
        ></v-select>
        <v-select
          :items="sources"
          label="Select Source"
          outlined
          item-text="label"
          item-value="value"
          dense
          style="flex-grow:0;width:200px"
          v-model="source"
        ></v-select>
        <v-text-field
          label="Date Created (dd/mm/yyyy)"
          v-model="dateCreated"
        ></v-text-field>
      </div>
    </transition>
  </HiDialog>
</template>

<script>
function preventDefault(e) {
  e.stopPropagation();
  e.preventDefault();
}

import cvs from "csv-parse";
import HiDialog from "../components/HiDialog";
import { meta } from "@/db";
import Validator from "../hive/Validator";
import { api } from "@/api";
import moment from "moment";
import hive from "@/hive";

export default {
  name: "HiCSVLoader",
  components: {
    HiDialog
  },
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: ".csv"
    }
  },
  data() {
    return {
      isDragOver: false,
      cvsKeys: null,
      cvsData: null,
      parseErr: null,
      colSelection: [],
      mappingSrc: [
        "title",
        "name",
        "midName",
        "surname",
        "email",
        "phone",
        "postcode",
        "dateCreated"
      ],
      mapping: [],
      duplicateMapping: [],
      isOpen: false,
      stage: 1,
      projects: null,
      sources: null,
      // leadsMeta: null,
      data: null,
      dataErr: null,
      project: null,
      source: null,
      dateCreated: null
    };
  },
  async created() {
    this.projects = await meta.projects();
    this.sources = await meta.sources();
    // console.log(this.projects);
    // console.log(this.sources);
  },
  computed: {
    disableSubmit() {
      // console.log("computing", this.stage, this.colSelection, this.mapping);
      // console.log("sources", this.leadsMeta ? this.leadsMeta.sources : null);
      if (this.stage === 1) {
        if (!this.cvsKeys) return true;
        if (!this.cvsData) return true;
        if (this.error) return true;
        if (!this.colSelection || this.colSelection.length === 0) return true;
        let selected = 0;
        for (let i = 0; i < this.colSelection.length; i++) {
          const v = this.colSelection[i];
          if (v) selected++;
          if (v && !this.mapping[i]) {
            return true;
          }
        }

        if (!selected) return true;

        for (let i = 0; i < this.duplicateMapping.length; i++) {
          if (this.duplicateMapping[i]) return true;
        }
        return false;
      } else if (this.stage === 3) {
        if (this.project && this.source) return false;
      }
      return true;
    }
  },
  methods: {
    open() {
      this.isOpen = true;
    },
    clickFileBtn() {
      this.$refs.input.click();
    },
    clear() {
      this.cvsKeys = null;
      this.cvsData = null;
      this.parseErr = null;
      this.colSelection = [];

      this.mapping = [];
      this.duplicateMapping = [];
      this.stage = 1;
      this.submitTxt = "Next";
      this.data = null;
      //   this.$refs.input.value = null;
    },
    mapChange() {
      this.duplicateMapping = [];
      for (let i = 0; i < this.mapping.length; i++) {
        const m1 = this.mapping[i];
        if (!m1) continue;
        for (let j = 0; j < this.mapping.length; j++) {
          if (i === j) continue;
          const m2 = this.mapping[j];

          if (m1 === m2) {
            this.duplicateMapping[i] = this.duplicateMapping[j] = true;
          }
        }
      }
    },
    dragEnter(e) {
      preventDefault(e);
    },
    dragOver(e) {
      preventDefault(e);
      this.isDragOver = true;
    },
    handleFile(file) {
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result;
        // console.log(text);
        cvs(
          text,
          {
            comment: "#"
          },
          (err, output) => {
            if (err) {
              this.parseErr = err;
            } else {
              this.cvsKeys = output[0];
              this.cvsData = output.splice(1);
              this.colSelection = [];
              //below is code for set all columns selected
              //   for (let i = 0; i < this.cvsKeys.length; i++) {
              //     this.colSelection[i] = true;
              //   }
            }
          }
        );
      };
      reader.readAsText(file);
      this.$refs.fileForm.reset();
    },
    drop(e) {
      //   console.log(e);
      preventDefault(e);
      this.isDragOver = false;
      const dt = e.dataTransfer;
      const files = dt.files;
      this.handleFile(files[0]);
      //   console.log(files);
    },
    fileInputChange(e) {
      if (e.target.files) {
        this.handleFile(e.target.files[0]);
      }
    },
    async submit() {
      if (this.stage === 1) {
        this.data = [];
        const err = [];
        let hasName = false,
          hasEmail = false;
        this.colSelection.forEach((selected, i) => {
          if (selected && this.mapping[i] === "name") hasName = true;
          else if (selected && this.mapping[i] === "email") hasEmail = true;
        });
        if (!hasName) {
          err.push({ error: "'name' must be provided." });
        }
        if (!hasEmail) {
          err.push({ error: "'email' must be provided." });
        }
        // console.log(hasName + " " + hasEmail);
        // console.log(err);
        for (let i = 0; i < this.cvsData.length; i++) {
          const arr = this.cvsData[i];
          const lead = {};
          for (let j = 0; j < this.colSelection.length; j++) {
            if (!this.colSelection[j]) continue;
            const key = this.mapping[j];
            let val = arr[j];
            if (key) {
              if (key === "email") {
                if (!val) {
                  err.push({ index: i, error: "Empty Email." });
                } else {
                  val = val.trim();
                  if (!Validator.email(val)) {
                    err.push({
                      index: i,
                      error: "Invalid Email: '" + val + "'."
                    });
                  }
                }
              } else if (key === "phone" && val) {
                val = val.trim();
                if (!val.length) {
                  val = null;
                } else if (!Validator.phoneNumber(val)) {
                  err.push({
                    index: i,
                    error: "Invalid Phone Number: '" + val + "'."
                  });
                }
              } else if (key === "dateCreated" && val) {
                const d = moment(val, "D/MM/YYYY");
                if (!d.isValid()) {
                  err.push({
                    index: i,
                    error: "Invalid Date: '" + val + "'."
                  });
                } else {
                  val = d.unix();
                }
              }
            }
            lead[key] = val;
          }
          if (!lead.name) {
            err.push({
              index: i,
              error: "Name must be provided."
            });
          }
          if (!/^\w+/.test(lead.name)) {
            err.push({
              index: i,
              error: `Invalid Name ${lead.name}.`
            });
          }
          this.data.push(lead);
        }
        this.dataErr = err;

        this.stage++;
        if (!err.length) this.stage++;
      } else if (this.stage === 3) {
        let date;
        if (this.dateCreated && this.dateCreated.trim().length) {
          date = moment(this.dateCreated, "D/MM/YYYY");
          if (!date.isValid()) {
            await hive.alert(`Date ${this.dateCreated} is not a valid date!`);
            return;
          }
        }

        const obj = {
          project: this.project,
          source: this.source,
          data: this.data
        };
        if (date) {
          obj.presets = { dateCreated: date.unix() };
        }
        console.log(obj);
        await api.leads.leadsImport(obj);
        this.clear();
        // this.isOpen = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.loader-block {
  height: 60vh;
}

.drop-area {
  border: 2px dashed #ababab;
  height: 100%;
  background-color: #e2e0d9;
  transition: opacity 0.5s;
}

.over {
  opacity: 0.5;
}

table {
  overflow-y: auto;
  max-height: 500px;
}

thead th {
  position: sticky;
  top: 0;
}

/* Just common table stuff. Really. */
table {
  border-collapse: collapse;
  width: 100%;
}

tbody {
  overflow-y: auto;
  max-height: 500px;
}

th,
td {
  padding: 8px 16px;
}

th {
  background: #eee;
}

::v-deep label {
  font-size: 10px !important;
}

::v-deep .check .v-messages {
  display: none;
}

::v-deep .cell {
  color: #aaaaaa;
}

::v-deep .cell.selected {
  color: #666666;
  background-color: #fefefe;
}

::v-deep .duplicate {
  background-color: #ffcccc !important;
}

::v-deep .unmapped {
  background-color: #eeeeee !important;
  color: #aaaaaa !important;
}

::v-deep .theme--light.v-data-table {
  background-color: #eeeeee;
}

.select {
  color: #777777;
  font-style: italic;
}
</style>
